/**
 * Tiny client-side sort helper for admin-ui tables.
 * Stores sort state per `tableId` in module-scope memory (lost on reload).
 */

type Dir = 'asc' | 'desc';

interface SortState {
  key: string;
  dir: Dir;
}

const stateByTable = new Map<string, SortState>();

export function getSortState(tableId: string): SortState | null {
  return stateByTable.get(tableId) ?? null;
}

export function cycleSort(tableId: string, key: string): SortState {
  const cur = stateByTable.get(tableId);
  let next: SortState;
  if (!cur || cur.key !== key) {
    next = { key, dir: 'asc' };
  } else if (cur.dir === 'asc') {
    next = { key, dir: 'desc' };
  } else {
    stateByTable.delete(tableId);
    return { key, dir: 'asc' };
  }
  stateByTable.set(tableId, next);
  return next;
}

export function applySort<T>(
  rows: T[],
  state: SortState | null,
  getter: (row: T, key: string) => unknown,
): T[] {
  if (!state) return rows;
  const out = [...rows];
  out.sort((a, b) => {
    const va = getter(a, state.key);
    const vb = getter(b, state.key);
    const cmp = compareCells(va, vb);
    return state.dir === 'asc' ? cmp : -cmp;
  });
  return out;
}

function compareCells(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  // Try bigint-as-string + number-as-string before falling back to lexicographic.
  if (typeof a === 'string' && typeof b === 'string') {
    const an = Number(a);
    const bn = Number(b);
    if (Number.isFinite(an) && Number.isFinite(bn) && !Number.isNaN(an) && !Number.isNaN(bn)) {
      if (an !== bn) return an - bn;
    }
    return a < b ? -1 : a > b ? 1 : 0;
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') return (a ? 1 : 0) - (b ? 1 : 0);
  return String(a).localeCompare(String(b));
}

/**
 * Build a `<th>` HTML cell that announces its sort key + current direction.
 * Pair with `wireSortHeaders(tableEl, tableId, onChange)` to make it clickable.
 */
export function sortableTh(
  label: string,
  key: string,
  state: SortState | null,
  opts: { numeric?: boolean } = {},
): string {
  let arrow = '';
  if (state?.key === key) arrow = state.dir === 'asc' ? ' ▲' : ' ▼';
  const cls = opts.numeric ? ' class="num"' : '';
  return `<th data-sort-key="${key}"${cls} style="cursor:pointer;user-select:none">${label}${arrow}</th>`;
}

/**
 * Wire click handlers on every `th[data-sort-key]` inside `tableEl`. Calls
 * `onChange(newState)` so the caller can re-render with the new ordering.
 */
export function wireSortHeaders(
  tableEl: HTMLElement,
  tableId: string,
  onChange: () => void,
): void {
  tableEl.querySelectorAll<HTMLElement>('th[data-sort-key]').forEach((th) => {
    th.addEventListener('click', () => {
      cycleSort(tableId, th.dataset.sortKey!);
      onChange();
    });
  });
}
